// problem-details.ts — R-06
// Mappe les erreurs ProblemDetails RFC 7807 vers des messages utilisateur en français.
// Le backend ASP.NET Core 10 retourne systématiquement ce format pour les erreurs.

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, string[]>;
  retryAfterSeconds?: number;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly detail?: string,
    public readonly raw?: ProblemDetails,
    public readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Messages HTTP génériques en français
const HTTP_MESSAGES: Record<number, string> = {
  400: "Données invalides. Vérifiez les informations saisies.",
  401: "Session expirée. Reconnectez-vous.",
  403: "Vous n'avez pas accès à cette ressource.",
  404: "La ressource demandée est introuvable.",
  409: "Un conflit empêche cette opération.",
  422: "Les données envoyées sont incorrectes.",
  423: "Compte temporairement verrouillé. Réessayez ultérieurement.",
  429: "Trop de requêtes. Attendez quelques instants.",
  500: "Une erreur serveur est survenue. Réessayez plus tard.",
  502: "Service temporairement indisponible.",
  503: "Service en maintenance. Réessayez plus tard.",
  504: "Le serveur met trop de temps à répondre. Réessayez plus tard.",
};

/**
 * Convertit une réponse ProblemDetails en ApiError avec message français.
 * Le `detail` du ProblemDetails est conservé pour les messages métier spécifiques
 * (ex : "Une version existe déjà pour cette année" sur un 409).
 */
export function mapProblemDetailsToApiError(
  status: number,
  body: unknown,
  retryAfterSeconds?: number,
): ApiError {
  // Garde contre null/undefined — l'API retourne toujours un objet mais on sécurise
  const problem = (
    body != null && typeof body === "object" ? body : {}
  ) as Partial<ProblemDetails>;
  // Message spécifique du serveur en priorité, sinon message générique
  const userMessage =
    problem.detail ??
    HTTP_MESSAGES[status] ??
    "Une erreur inattendue est survenue.";
  const effectiveRetryAfterSeconds =
    problem.retryAfterSeconds ?? retryAfterSeconds;
  return new ApiError(
    status,
    userMessage,
    problem.detail,
    problem as ProblemDetails,
    effectiveRetryAfterSeconds,
  );
}
